import EmptyState from 'components/EmptyState/EmptyState';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import 'features/Weather/assets/weatherDataDisplay.css';
import { getForecastDateLabel } from 'features/Weather/utils/forecastDate.utils';
import useLocation from 'hooks/useLocation';
import { useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { MdOutlineSurfing } from 'react-icons/md';
import { useGetDetailedMarine7Query } from 'services/weather/marine/marine.api';
import MarineConditionPanel from '../../components/MarineConditionPanel/MarineConditionPanel';
import { displayMarineValue } from '../../utils/marineForecast.utils';
import './assets/surfAlerts.scss';
import { route, surfAlertDetails } from './config/surfAlerts.config';
import { getSurfAlertDayGroups } from './utils/surfAlerts.utils';

const SurfAlerts = () => {
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const {
    data: detailedMarine = [],
    isLoading: isLoadingDetailedMarine,
    isFetching: isFetchingDetailedMarine,
  } = useGetDetailedMarine7Query(effectiveLocation);
  const isRefreshing = isFetchingDetailedMarine && !isLoadingDetailedMarine;

  const surfAlertDayGroups = useMemo(() => getSurfAlertDayGroups(detailedMarine), [detailedMarine]);

  return (
    <Container
      fluid
      className="surf-alerts weather-data-display d-flex h-100 flex-column gap-3 px-0 text-white user-select-none"
    >
      <WidgetTimeUpdate route={route} />
      {isLoadingDetailedMarine ? (
        <div className="d-flex h-100 align-items-center justify-content-center mt-3">
          <WidgetLoadingAnimation />
        </div>
      ) : !surfAlertDayGroups.length ? (
        <EmptyState
          title="Surf Alerts"
          message="No surf alert data available for the selected day."
        />
      ) : (
        <>
          <div className="weather-data-display-heading d-flex align-items-start justify-content-between gap-3 border-bottom pb-2">
            <h2 className="weather-data-display-title m-0 fw-bold lh-sm">
              {getForecastDateLabel(new Date(), 'Current Surf Alerts') ?? 'Current Surf Alerts'}
            </h2>
            {isRefreshing && (
              <span className="surf-alerts-page-status small" role="status">
                Updating...
              </span>
            )}
          </div>
          <div className="surf-alerts-scroll flex-grow-1 overflow-auto">
            {surfAlertDayGroups.length ? (
              <div className="d-flex flex-column gap-4 pb-4">
                {surfAlertDayGroups.map((dayGroup) => (
                  <MarineConditionPanel
                    key={dayGroup.dayTimestamp}
                    title={dayGroup.weekdayLabel}
                    dateLabel={dayGroup.dateLabel}
                    marineData={dayGroup.marineData}
                    details={surfAlertDetails}
                    hasPanelChrome={false}
                    containerClassName="surf-alerts-day gap-2"
                    headerClassName="surf-alerts-day-header"
                    titleClassName="surf-alerts-day-title fs-5"
                    cardListClassName="weather-time-period-grid"
                    cardContainerClassName="surf-alerts-time-card"
                    getCardTitle={(entry) => entry.day_segment}
                    getCardSubtitle={(entry) => displayMarineValue(entry.surf, null)}
                    getCardIcon={() => <MdOutlineSurfing size={56} />}
                    headingLevel="h3"
                    cardHeadingLevel="h4"
                  />
                ))}
              </div>
            ) : (
              <p className="m-0 d-flex h-100 align-items-center justify-content-center text-secondary">
                No surf alert data available.
              </p>
            )}
          </div>
        </>
      )}
    </Container>
  );
};

export default SurfAlerts;
